// The conditions editor's lists and sentences, as pure functions.
//
// A pure module rather than logic inside the component, for this repository's standing
// reason: there is no DOM harness, so anything decidable without a DOM is decided here
// and asserted by test/web/form-conditions-edit.test.js — the shape form-fields.js,
// descriptor-form.js and queue-members.js established.
//
// WHAT THIS SCREEN DECIDES is which questions an agent is NOT asked. Every failure mode
// is quiet in the same direction: a condition whose value no longer matches any choice
// simply never fires, so its fields are never shown, and nothing errors anywhere. The
// sentences below exist to make that visible before it is saved rather than after a month
// of tickets is missing an answer.

/** Zendesk's anchor types, mirrored from src/services/admin/form-conditions.js. */
export const ANCHOR_TYPES = ['select', 'checkbox', 'text', 'textarea'];

/**
 * The fields on this form that can DECIDE a condition.
 *
 * A condition compares one stored string to the parent's answer, which only means
 * something for a field whose answers are a known set or a typed value. A `multiselect`
 * is excluded even though the applier copes with one, because Zendesk does not offer it
 * as an anchor and authoring one would build a form the desk's own export cannot
 * round-trip.
 */
export function anchorCandidates(formFields) {
  return (formFields ?? []).filter((field) => ANCHOR_TYPES.includes(field.data_type));
}

/**
 * The answers a parent can take, when they are knowable.
 *
 * A select offers its own choices, so the value cannot be mistyped — which matters more
 * than it sounds: a typo'd value is a condition that never fires, and a condition that
 * never fires looks exactly like one that has not been triggered yet. A checkbox offers
 * true/false. A text field cannot be enumerated, so it stays free text.
 */
export function valueChoicesFor(field) {
  if (!field) return null;
  if (field.data_type === 'checkbox') {
    return [{ value: 'true', label: 'Ticked' }, { value: 'false', label: 'Not ticked' }];
  }
  if (field.data_type === 'select' && (field.options ?? []).length > 0) {
    return field.options.map((option) => ({ value: option.value, label: option.label }));
  }
  return null;
}

/**
 * The fields a condition may reveal: everything on the form except its own parent.
 *
 * A field that reveals itself can never appear — it is hidden until its own answer is
 * given, and it cannot be answered while hidden.
 */
export function childCandidates(formFields, parentFieldId) {
  return (formFields ?? []).filter((field) => String(field.id) !== String(parentFieldId));
}

/**
 * Per-row problems, checked here so the editor never posts what the server has already
 * said it will refuse — and so the admin sees all of them at once rather than one per
 * round trip.
 */
export function conditionProblems(condition, formFields) {
  const problems = [];
  const parent = (formFields ?? []).find((f) => String(f.id) === String(condition.parent_field_id));

  if (!condition.parent_field_id) problems.push('Choose the field that decides this.');
  else if (!parent) problems.push('That field is no longer on this form.');
  else if (!ANCHOR_TYPES.includes(parent.data_type)) {
    problems.push(`A ${parent.data_type} field cannot decide a condition.`);
  }

  if (!String(condition.value ?? '').trim()) problems.push('Choose the answer that reveals the fields.');
  if ((condition.children ?? []).length === 0) problems.push('Choose at least one field to reveal.');

  // A value that matches none of the parent's choices can never be true. It is storable,
  // it looks right in the list, and the fields it governs simply never appear.
  const choices = valueChoicesFor(parent);
  if (choices && condition.value && !choices.some((c) => c.value === condition.value)) {
    problems.push(`"${condition.value}" is not one of that field's choices, so this can never fire.`);
  }

  return problems;
}

/** A one-line reading of the rule, so an admin checks meaning rather than fields. */
export function describeCondition(condition, formFields) {
  const parent = (formFields ?? []).find((f) => String(f.id) === String(condition.parent_field_id));
  if (!parent) return 'Incomplete condition.';
  const choices = valueChoicesFor(parent);
  const valueLabel = choices?.find((c) => c.value === condition.value)?.label ?? condition.value;
  const names = (condition.children ?? [])
    .map((child) => (formFields ?? []).find((f) => String(f.id) === String(child.id))?.label)
    .filter(Boolean);
  if (names.length === 0) return `When ${parent.label} is "${valueLabel}" — nothing yet.`;

  const required = (condition.children ?? []).filter((c) => c.is_required).length;
  const also = required > 0 ? ` (${required} required)` : '';
  return `When ${parent.label} is "${valueLabel}", also ask ${names.join(', ')}${also}.`;
}

/**
 * The child list, narrowed by what the admin typed.
 *
 * WHY THIS EXISTS. Support - Default Ticket Form carries 42 fields and 21 conditions, so
 * the unfiltered editor draws about 860 checkboxes on one page; the Integrations form is
 * 49 and 15. Finding "Live Date" in that is a scroll, and a scroll is how somebody ticks
 * the wrong box.
 *
 * A TICKED FIELD IS NEVER HIDDEN, whatever the search says. Filtering it out of view
 * would leave the admin looking at a condition that reads "also ask 4 fields" while
 * showing one, and the natural next move — ticking what appears to be missing — edits a
 * rule they cannot see. Matches and existing choices, always.
 *
 * Matched on label and key both: an admin who knows the field as `live_date` should not
 * have to remember it is labelled "Live Date".
 */
export function filterChildren(candidates, search, chosenIds = []) {
  const needle = String(search ?? '').trim().toLowerCase();
  if (needle === '') return candidates ?? [];
  const chosen = new Set((chosenIds ?? []).map(String));
  return (candidates ?? []).filter((field) => chosen.has(String(field.id))
    || String(field.label ?? '').toLowerCase().includes(needle)
    || String(field.key ?? '').toLowerCase().includes(needle));
}

/**
 * What a collapsed row has to say to be worth collapsing.
 *
 * A row is shut by default — 21 open rows is the wall this is fixing — so the summary IS
 * the row for most of a session. It carries the count, because "also ask" with no number
 * gives no reason to open it, and the problem count, because a row that cannot be saved
 * must announce itself while shut or the Save button refuses for an invisible reason.
 */
export function collapsedSummary(condition, formFields, problems = []) {
  const count = (condition?.children ?? []).length;
  const fields = count === 1 ? '1 field' : `${count} fields`;
  const base = `${describeCondition(condition, formFields).replace(/\.$/, '')} — ${fields}`;
  if (problems.length === 0) return base;
  return `${base} — ${problems.length === 1 ? '1 problem' : `${problems.length} problems`}`;
}

/** An empty row, so "Add" produces something the editor can render immediately. */
export function blankCondition() {
  return { parent_field_id: '', value: '', audience: 'agent', children: [] };
}

/** The wire shape the PUT takes. Order is the array order, as it is for the field list. */
export function toWire(conditions) {
  return (conditions ?? []).map((condition) => ({
    parent_field_id: String(condition.parent_field_id),
    value: String(condition.value ?? ''),
    audience: condition.audience === 'end_user' ? 'end_user' : 'agent',
    children: (condition.children ?? []).map((child) => ({
      id: String(child.id),
      is_required: child.is_required === true,
    })),
  }));
}

/** What the GET returns, as the editor holds it. */
export function fromWire(rows) {
  return (rows ?? []).map((row) => ({
    parent_field_id: String(row.parent_field_id),
    value: row.value,
    audience: row.audience,
    children: (row.children ?? []).map((child) => ({
      id: String(child.id),
      is_required: child.is_required === true,
    })),
  }));
}
